import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  inject,
} from "@angular/core";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { assetsConst } from "src/app/core/const/assets.const";
import { SignupComponent } from "../signup/signup.component";
import { ForgotPasswordComponent } from "../forgot-password/forgot-password.component";
import { VerificationComponent } from "../verification/verification.component";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from "@angular/material/checkbox";
import { AuthService } from "src/app/core/services/auth.service";
import { SpinnerService } from "src/app/core/services/spinner.service";
import { ToasterService } from "src/app/core/services/toastr.service";
import { environment } from "src/environments/environment";
import { REGEX } from "src/app/core/const/regex.constant";
import { CountryCodeConst } from "src/app/core/const/countryCodeConst.const";
import { countryCode } from "src/app/models/countryCode";
import { LoginBody, ProfileDetails } from "src/app/models/authmodel";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { NgxMatIntlTelInputComponent } from "ngx-mat-intl-tel-input";
import {
  GoogleSigninButtonModule,
  SocialAuthService,
} from "@abacritt/angularx-social-login";

@Component({
  selector: "megastore-login",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
    NgxMatIntlTelInputComponent,
    MatCheckboxModule,
    MatDialogModule,
    GoogleSigninButtonModule,
  ],

  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild("phone") phoneField!: ElementRef | any;
  hide = true;
  isRemembered = false;
  loginFormGroup: FormGroup;
  submitted = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  assetConst = assetsConst;
  countryCodeObject: countryCode = CountryCodeConst;
  loginWithEmail = true;
  private platFormId = inject(PLATFORM_ID);
  fcmToken: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private _authService: AuthService,
    private _toasterService: ToasterService,
    private _socialLoginService: SocialAuthService,
    private _spinnerService: SpinnerService,
    private dialog: MatDialog,
    private _translateService: TranslateService,
    private dialogRef: MatDialogRef<LoginComponent>
  ) {
    this._authService.FCMtoken.subscribe((res) => (this.fcmToken = res));
    this.loginFormGroup = this.formBuilder.group({
      email: ["", [Validators.required, Validators.pattern(REGEX.Email)]],
      password: ["", Validators.required],
      phoneNo: [
        {
          value: "",
          disabled: true,
        },
      ],
      dialCode: [
        {
          value: "",
          disabled: true,
        },
      ],
    });
    this.socialLogin();
  }

  socialLogin(): void {
    // Clear any existing Google session first
    this._socialLoginService?.signOut();

    // Then trigger the sign-in process
    this._socialLoginService?.signIn("google");
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platFormId)) {
      const rememberMeData = localStorage.getItem(environment.rememberMeKey);
      if (rememberMeData) {
        this.isRemembered = true;

        const body = JSON.parse(rememberMeData);

        this.loginFormGroup.patchValue({
          email: body.email,
          password: body.password,
        });
      }
    }

    // Subscribe to social auth state changes
    this._socialLoginService.authState
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          const userData: {
            name: string;
            email: string;
            googleId: string;
          } = {
            name: user.name,
            email: user.email,
            googleId: user.id,
          };
          this._authService
            .socialLogin(userData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response: ProfileDetails) => {
                localStorage.setItem(
                  environment.storageKey,
                  response.accessToken
                );
                sessionStorage.removeItem(environment.guestTokenKey);
                this._toasterService.successToastr(
                  this._translateService.instant("LoginSuccess")
                );
                this._authService.profileDetails.next(response);
                this._authService.loggedIn.next(true);
                this.dialogRef.close(true);
              },
              error: () => {
                this._spinnerService.removeFromLoader("login");
              },
            });
        }
      });
  }

  toggleRememberMe(event: MatCheckboxChange): void {
    this.isRemembered = event["checked"];
  }

  onSubmit(): void {
    this.submitted = true;
    let payload: LoginBody;
    if (this.loginWithEmail) {
      this.loginFormGroup.patchValue({
        email: this.loginFormGroup.get("email")?.value.trim(),
      });
      payload = {
        email: this.loginFormGroup.get("email")?.value,
        password: this.loginFormGroup.get("password")?.value,
        deviceToken: this.fcmToken,
        deviceType: "WEB",
      };
    } else {
      const modifiedValue = this.patchValues();
      payload = {
        phoneNo: modifiedValue.phoneNo,
        dialCode: modifiedValue.dialCode,
        // deviceToken: this.fcmToken,
        // deviceType: 'WEB'
      };
    }

    if (this.loginFormGroup.valid) {
      this.onFormValid(payload);
    }
  }

  onFormValid(payload: LoginBody): void {
    if (isPlatformBrowser(this.platFormId)) {
      this._spinnerService.addToLoader("login");
      (this.loginWithEmail
        ? this._authService.loginUserByEmail(payload)
        : this._authService.loginUserByPhone(payload)
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: ProfileDetails) => {
            this._spinnerService.removeFromLoader("login");

            if (isPlatformBrowser(this.platFormId)) {
              if (this.loginWithEmail) {
                localStorage.setItem(
                  environment.storageKey,
                  response.accessToken
                );
                sessionStorage.removeItem(environment.guestTokenKey);
                this._toasterService.successToastr(
                  this._translateService.instant("LoginSuccess")
                );
                this._authService.profileDetails.next(response);
                if (this.isRemembered) {
                  const data = {
                    email: this.loginFormGroup.controls["email"]["value"],
                    password:
                      this.loginFormGroup["controls"]["password"]["value"],
                  };
                  if (!localStorage.getItem(environment.rememberMeKey)) {
                    localStorage.setItem(
                      environment.rememberMeKey,
                      JSON.stringify(data)
                    );
                  }
                } else {
                  localStorage.removeItem(environment.rememberMeKey);
                }
                this._authService.loggedIn.next(true);
              } else {
                this._toasterService.successToastr(
                  this._translateService.instant("otpSentSuccesss")
                );
                this.dialog.open(VerificationComponent, {
                  panelClass: ["modall", "login_nodal"],
                  data: {
                    ...payload,
                    length: 4,
                    type: "login",
                  },
                });
              }
              this.dialogRef.close(true);
            }
          },
          error: () => {
            this._spinnerService.removeFromLoader("login");
          },
        });
    }
  }

  loginTypeChanged(): void {
    this.loginWithEmail = !this.loginWithEmail;
    this.submitted = false;

    if (this.loginWithEmail) {
      this.loginFormGroup.get("email")?.enable();
      this.loginFormGroup.get("password")?.enable();
      this.loginFormGroup
        .get("email")
        ?.setValidators([Validators.required, Validators.pattern(REGEX.Email)]);
      this.loginFormGroup
        .get("password")
        ?.setValidators([
          Validators.required,
          Validators.pattern(REGEX.Password),
        ]);

      this.loginFormGroup.get("dialCode")?.disable();
      this.loginFormGroup.get("phoneNo")?.disable();

      this.loginFormGroup.updateValueAndValidity();
    } else {
      this.loginFormGroup.get("email")?.disable();
      this.loginFormGroup.get("password")?.disable();
      this.loginFormGroup.get("dialCode")?.enable();
      this.loginFormGroup.get("phoneNo")?.enable();
      this.loginFormGroup.get("phoneNo")?.setValidators([Validators.required]);

      this.loginFormGroup.updateValueAndValidity();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countryChangedEvent(event: any): void {
    this.countryCodeObject = event;
  }

  SignUp(): void {
    this.dialogRef.close();
    this.dialog.open(SignupComponent, {
      panelClass: ["modall", "login_nodal"],
    });
  }

  forgotPass(): void {
    this.dialogRef.close();
    this.dialog.open(ForgotPasswordComponent, {
      panelClass: ["modall", "login_nodal"],
    });
  }

  patchValues(): LoginBody {
    // eslint-disable-next-line prefer-const
    let value: LoginBody;

    const patchValueToCountryCode = (): void => {
      let tempCountryCode = this.loginFormGroup.get("dialCode")?.value;

      if (tempCountryCode) {
        tempCountryCode = tempCountryCode.split("");
        tempCountryCode.shift();
        tempCountryCode = tempCountryCode.join("");
      }

      if (tempCountryCode !== this.countryCodeObject.dialCode) {
        this.loginFormGroup.patchValue({
          dialCode: `+${this.countryCodeObject.dialCode}`,
        });
      }
    };

    const patchValueToPhoneField = (): void => {
      let phone = this.loginFormGroup.get("phoneNo")?.value;

      if (phone && phone.includes(this.loginFormGroup.get("dialCode")?.value)) {
        phone = phone.split(this.loginFormGroup.get("dialCode")?.value);
        if (value.phoneNo !== undefined) {
          value.phoneNo = phone[1].trim();
        }
      }
    };

    patchValueToCountryCode();
    value = this.loginFormGroup["value"];
    patchValueToPhoneField();

    return value;
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
